import { Controller } from '@hotwired/stimulus';

// cvr_desktop_ledger_092326. The flag is evaluated per device but the two-column layout is
// per viewport, so the server renders the single column for both arms and this moves the
// ledger, the assurances and what-to-expect into the sticky panel at >=992px. Below that it
// moves nothing, which is what keeps a narrow window identical to the control arm.
export default class extends Controller {
  static targets = ['panel'];

  connect() {
    this.built = false;
    this.column = this.element.querySelector('.m-checkout-mf__column');
    // Never put an iframe inside these — moving a node reloads it and unmounts Stripe.
    this.pieces = [
      this.capture(document.getElementById('order-summary-container'), 'panel'),
      this.capture(this.element.querySelector('.m-checkout-mf-assurances'), 'panel'),
      this.capture(document.querySelector('.m-checkout-mf-expect'), 'column'),
    ].filter(Boolean);

    if (!this.column || !this.hasPanelTarget || this.pieces.length === 0) return;

    this.query = window.matchMedia('(min-width: 992px)');
    this.onQueryChange = () => this.apply();
    this.query.addEventListener('change', this.onQueryChange);
    // Backstop: a missed change event used to leave the panel empty until a reload.
    this.onResize = () => this.scheduleApply();
    window.addEventListener('resize', this.onResize);
    this.apply();
  }

  disconnect() {
    // connect() bails before any of these exist when the markup is missing a hook.
    if (!this.query) return;

    this.query.removeEventListener('change', this.onQueryChange);
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.frame);
  }

  scheduleApply() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.apply());
  }

  // Remember where each piece came from, so crossing back under 992px restores the column.
  capture(element, destination) {
    if (!element) return null;

    return {
      element,
      destination,
      parent: element.parentNode,
      next: element.nextSibling,
    };
  }

  // Idempotent: it reads where each node actually is, so calling it on every resize tick
  // converges the layout instead of trusting one event to have fired.
  apply() {
    const desktop = this.query.matches;

    this.pieces.forEach((piece) => this.move(piece, desktop));
    // Gates the grid, so the page never paints two columns around an empty panel.
    document.body.classList.toggle('checkout-ledger-ready', desktop);

    if (desktop === this.built) return;

    // Only on the crossing. Doing it every tick would reopen a summary the customer closed.
    if (desktop) {
      this.summary?.setAttribute('open', '');
    } else {
      this.summary?.removeAttribute('open');
    }
    this.built = desktop;
  }

  move({ element, destination, parent, next }, desktop) {
    const target = desktop
      ? destination === 'panel'
        ? this.panelTarget
        : this.column
      : parent;

    if (element.parentNode === target) return;

    // `next` can have moved since connect, and insertBefore throws on a foreign node.
    const before = !desktop && next?.parentNode === target ? next : null;
    target.insertBefore(element, before);
  }

  get summary() {
    return document.getElementById('order-summary-container');
  }
}
